import { SlideClassificationLesionLine } from "pwapi";

import { LesionLine, Subtype } from "../reducers/aiReducer";

export type PositiveLesionLine = {
  diagnosis_name: string;
  linesOfName: SlideClassificationLesionLine[];
  subtypes: string[];
  slide_ids: (number | undefined)[];
};

export const LesionLines = {
  SK: { name: "Seborrheic Keratosis", subtypes: {} },
  BCC: {
    name: "Basal Cell Carcinoma",
    subtypes: {
      NOD: "Nodular",
      INF: "Infiltrative",
      SUP: "Superficial",
    },
  },
  ASL: {
    name: "Atypical Squamous Lesion",
    subtypes: {
      SCC: "Invasive Squamous Cell Carcinoma",
      SCC_IS: "Squamous Cell Carcinoma In-Situ",
      AK: "Actinic Keratosis",
      HAK: "Hypertrophic Actinic Keratosis",
      AIP: "Atypical Intraepidermal Proliferation",
      ASP: "Atypical Squamous Proliferation",
      SCC_ALIS: "SCC, At Least In-Situ",
    },
  },
  MEL: {
    name: "Melanocytic Lesion",
    subtypes: {
      LG_MEL: "Mild Atypical Melanocytic Lesion",
      HG_MEL: "Severe Atypical Melanocytic Lesion",
      MEL_JUNC_NEV: "Benign Junctional Nevus",
      MEL_DERMAL_NEV: "Benign Dermal Nevus",
      MEL_COMP_NEV: "Benign Compound Nevus",
    },
  },
  CYST: {
    name: "Cyst",
    subtypes: {
      PIC: "Pilar Cyst",
      EIC: "Epidermal Inclusion Cyst",
    },
  },
  VV: { name: "Verruca Vulgaris", subtypes: {} },
  // Not really a lesion line (unsure where this is used)
  INC: { name: "Inconclusive", subtypes: {} },
};

export const MacroCodes = {
  "Basal Cell Carcinoma": {
    // There is no "BCC" alone... BCC must have a subtype
    BCCN: [LesionLines.BCC.subtypes.NOD],
    BCCI: [LesionLines.BCC.subtypes.INF],
    BCCS: [LesionLines.BCC.subtypes.SUP],
    BCCNI: [LesionLines.BCC.subtypes.NOD, LesionLines.BCC.subtypes.INF],
    BCCSN: [LesionLines.BCC.subtypes.SUP, LesionLines.BCC.subtypes.NOD],
    BCCSI: [LesionLines.BCC.subtypes.SUP, LesionLines.BCC.subtypes.INF],
    BCCSNI: [
      LesionLines.BCC.subtypes.SUP,
      LesionLines.BCC.subtypes.NOD,
      LesionLines.BCC.subtypes.INF,
    ],
  },
  "Melanocytic Lesion": {
    CNMILD: [LesionLines.MEL.subtypes.LG_MEL],
  },
  "Atypical Squamous Lesion": {
    WDSCC: [LesionLines.ASL.subtypes.SCC],
    SCCIS: [LesionLines.ASL.subtypes.SCC_IS],
    AK: [LesionLines.ASL.subtypes.AK],
    AKE: [LesionLines.ASL.subtypes.AIP],
    FISAHKSUP: [LesionLines.ASL.subtypes.ASP],
    SCCISAT: [LesionLines.ASL.subtypes.SCC_ALIS],
  },

  "Seborrheic Keratosis": {
    SKII: [], // no subtypes
  },
  "Verruca Vulgaris": {
    VVII: [], // no subtypes
  },
  Cyst: {
    EIC: [LesionLines.CYST.subtypes.EIC],
    PC: [LesionLines.CYST.subtypes.PIC],
  },
};

export const NamedLesionLines = Object.fromEntries(
  Object.entries(LesionLines).map(([key, value]) => [
    value.name,
    {
      ...value,
      shortname: key,
      subtypes: Object.fromEntries(
        Object.entries(value.subtypes).map(([key, value]) => [value, key])
      ),
    },
  ])
);

export const macroCodeForLine = (
  lesionLine?: LesionLine & { subtype?: Subtype }
) => {
  // NOTE: deprecated for macroCodesWithSubtypePossibilities
  if (!lesionLine) {
    return undefined;
  }

  const subtypes: Subtype[] =
    lesionLine.hasOwnProperty("subtype") && lesionLine.subtype
      ? [lesionLine.subtype]
      : lesionLine.hasOwnProperty("subtypes") && lesionLine.subtypes
      ? lesionLine.subtypes
      : [];
  const codes =
    MacroCodes[lesionLine.diagnosis_name as keyof typeof MacroCodes];
  if (!codes) {
    return undefined;
  }

  const dxcode = Object.entries(codes).find(
    ([code, requires]) =>
      requires?.length === subtypes?.length &&
      Object.values(requires).every((subtype) =>
        subtypes.map((s) => s.diagnosis).includes(subtype as string)
      ) &&
      code
  );

  return dxcode ? dxcode[0] : undefined;
};

export const macroCodesWithSubtypePossibilities = (
  positiveLegionLine: PositiveLesionLine,
  lesionLine?: LesionLine & { subtype?: Subtype } // NOTE: & { subtype?: Subtype } is for deprecated single-subtype slides. Could possibly be removed
) => {
  if (!lesionLine) {
    return undefined;
  }

  const subtypes: Subtype[] =
    lesionLine.hasOwnProperty("subtype") && lesionLine.subtype
      ? [lesionLine.subtype]
      : lesionLine.hasOwnProperty("subtypes") && lesionLine.subtypes
      ? lesionLine.subtypes
      : [];
  const positive_subtypes = subtypes?.filter(
    (s) => s.diagnosis_result === "POSITIVE"
  );
  const inconclusive_subtypes = subtypes?.filter(
    (s) => s.diagnosis_result === "INCONCLUSIVE"
  );

  const codes =
    MacroCodes[lesionLine.diagnosis_name as keyof typeof MacroCodes];
  if (!codes) {
    return undefined;
  }

  const positive_dxcode = Object.entries(codes).find(
    ([code, requires]) =>
      requires?.length === positive_subtypes?.length &&
      Object.values(requires).every((subtype) =>
        positive_subtypes.map((s) => s.diagnosis).includes(subtype as string)
      ) &&
      code
  );
  const inconclusive_dxcodes = Object.entries(codes).find(
    ([code, requires]) =>
      requires?.length === inconclusive_subtypes?.length &&
      Object.values(requires).every((subtype) =>
        subtypes.map((s) => s.diagnosis).includes(subtype as string)
      ) &&
      code
  );
  // TODO this needs to be a "Data structure"
  // https://app.shortcut.com/pathologywatch/story/9716/display-all-possible-macro-code-outputs-via-multiple-subtypes
  // e.g.
  // return [
  //   positive_dxcode ? positive_dxcode[0] : undefined,
  //   ...inconclusive_dxcodes,
  // ].filter((v) => v);
  return positive_dxcode
    ? { line: positiveLegionLine, code: positive_dxcode[0] }
    : undefined;
};
